import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CustomerEditDialog from "@/components/customers/CustomerEditDialog";
import SuppliesPanel from "@/components/supplies/SuppliesPanel";
import OpportunitiesPanel from "@/components/opportunities/OpportunitiesPanel";
import QuotesPanel from "@/components/quotes/QuotesPanel";
import AppointmentsPanel from "@/components/appointments/AppointmentsPanel";
import ActivitiesPanel from "@/components/activities/ActivitiesPanel";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/brand/StatusBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Pencil, Archive, RotateCcw, UserCircle, Building, Home, Phone, Mail,
  MapPin, Briefcase, Flame, Target, FileText, CalendarDays, CheckSquare, History, Shield,
  Clock, User as UserIcon, Zap, MessageCircle, Trash2,
} from "lucide-react";

const TYPE_ICON = { Privato: UserCircle, Azienda: Building, Condominio: Home };
const TABS = [
  { id: "panoramica", label: "Panoramica", icon: UserCircle },
  { id: "luce-gas", label: "Luce/Gas", icon: Flame },
  { id: "efficienza", label: "Efficienza", icon: Zap },
  { id: "preventivi", label: "Preventivi", icon: FileText },
  { id: "appuntamenti", label: "Appuntamenti", icon: CalendarDays },
  { id: "attivita", label: "Attività", icon: CheckSquare },
  { id: "storico", label: "Storico", icon: History },
];

function Field({ label, value }) {
  return (
    <div>
      <div className="ds-label">{label}</div>
      <div className="mt-0.5 text-sm text-slate-800">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="ds-card flex items-center gap-3 p-4">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
      <div><div className="font-display text-xl font-bold text-slate-900">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
    </div>
  );
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("panoramica");
  const [editOpen, setEditOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  // Fetch principale legato all'id: al cambio cliente si resetta subito lo stato
  // (cliente, storico, loading) e si abortisce la richiesta precedente tramite
  // AbortController. Una risposta "vecchia" non può mai sovrascrivere il cliente
  // nuovo: se l'id cambia la richiesta viene abortita e il suo esito ignorato.
  useEffect(() => {
    const controller = new AbortController();
    setC(null);
    setHistory([]);
    setLoading(true);
    api.get(`/customers/${id}`, { signal: controller.signal })
      .then(({ data }) => setC(data))
      .catch((e) => {
        if (controller.signal.aborted || e.code === "ERR_CANCELED" || e.name === "CanceledError") return;
        toast.error("Cliente non trovato");
        navigate("/clienti");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [id, navigate]);

  // Storico legato a tab + id, anch'esso cancellabile al cambio.
  useEffect(() => {
    if (tab !== "storico") return;
    const controller = new AbortController();
    api.get(`/customers/${id}/history`, { signal: controller.signal })
      .then(({ data }) => setHistory(data))
      .catch(() => {});
    return () => controller.abort();
  }, [tab, id]);

  // Ricarica manuale del cliente corrente (dopo archivia/riattiva).
  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${id}`);
      setC(data);
    } catch { /* ignora */ }
  }, [id]);

  const doArchive = async () => {
    setBusy(true);
    try { await api.post(`/customers/${id}/archive`); toast.success("Cliente archiviato"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const doReactivate = async () => {
    setBusy(true);
    try { await api.post(`/customers/${id}/reactivate`); toast.success("Cliente riattivato"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const doHardDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/customers/${id}/hard`);
      toast.success("Cliente eliminato definitivamente");
      navigate("/clienti");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); setBusy(false); }
  };

  if (loading || !c) return <div className="flex items-center justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>;

  const Icon = TYPE_ICON[c.type] || UserCircle;
  const counts = c.counts || {};

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="customer-360">
      <button onClick={() => navigate("/clienti")} className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-strong" data-testid="back-to-customers">
        <ArrowLeft className="h-4 w-4" /> Torna ai clienti
      </button>

      {/* Header cliente */}
      <div className="ds-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Icon className="h-7 w-7" /></span>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="customer-name">{c.display_name}</h1>
                {c.archived
                  ? <StatusBadge type="danger" icon={Archive} testid="status-archived">Archiviato</StatusBadge>
                  : <StatusBadge type="success" testid="status-active">Attivo</StatusBadge>}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{c.type}</span>
                {c.phone_primary && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {c.phone_primary}</span>}
                {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {c.email}</span>}
                {c.comune && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.comune}{c.provincia ? ` (${c.provincia})` : ""}</span>}
                {c.office_name && <span className="inline-flex items-center gap-1"><Building className="h-3.5 w-3.5" /> {c.office_name}</span>}
                {c.operator_name && <span className="inline-flex items-center gap-1"><UserIcon className="h-3.5 w-3.5" /> {c.operator_name}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {c.phone_primary && (
              <Button variant="outline" onClick={() => navigate(`/whatsapp?customer=${id}`)} className="gap-1.5 border-success/40 text-success hover:bg-success/10 hover:text-success" data-testid="customer-whatsapp-button"><MessageCircle className="h-4 w-4" /> WhatsApp</Button>
            )}
            <Button onClick={() => setEditOpen(true)} className="gap-1.5" data-testid="edit-customer-button"><Pencil className="h-4 w-4" /> Modifica</Button>
            {isAdmin && (c.archived ? (
              <Button variant="outline" onClick={doReactivate} disabled={busy} className="gap-1.5" data-testid="reactivate-button"><RotateCcw className="h-4 w-4" /> Riattiva</Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="gap-1.5 text-danger hover:text-danger" data-testid="archive-button"><Archive className="h-4 w-4" /> Archivia</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white" data-testid="archive-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archiviare questo cliente?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {c.display_name} non apparirà più tra i clienti attivi né nella ricerca. Nessun dato o storico verrà eliminato: potrai riattivarlo in qualsiasi momento.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="archive-cancel">Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={doArchive} data-testid="archive-confirm-btn">Archivia</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ))}
            {isAdmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="gap-1.5 border-danger/40 bg-danger/5 text-danger hover:bg-danger/10 hover:text-danger" data-testid="hard-delete-button"><Trash2 className="h-4 w-4" /> Elimina definitivamente</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white" data-testid="hard-delete-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Eliminare definitivamente «{c.display_name}»?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Azione <strong>irreversibile</strong>. Verranno cancellati definitivamente il cliente <strong>«{c.display_name}»</strong> e <strong>tutti</strong> i dati collegati: forniture, opportunità, preventivi, appuntamenti, attività, storico, consensi, allegati, voci della Coda Lavori e notifiche. Usa questa funzione solo per i clienti di <strong>prova</strong>. Per i clienti reali usa “Archivia”.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="hard-delete-cancel">Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={doHardDelete} disabled={busy} className="bg-danger text-white hover:bg-danger/90" data-testid="hard-delete-confirm-btn">Elimina definitivamente</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      {/* Card riepilogative */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <SummaryCard icon={Flame} label="Forniture attive" value={counts.supplies_active ?? 0} tone="bg-warning/10 text-warning" />
        <SummaryCard icon={Target} label="Opportunità aperte" value={counts.opportunities_open ?? 0} tone="bg-brand/10 text-brand" />
        <SummaryCard icon={FileText} label="Preventivi" value={counts.quotes ?? 0} tone="bg-brand/10 text-brand" />
        <SummaryCard icon={CalendarDays} label="Appuntamenti" value={counts.appointments ?? 0} tone="bg-brand/10 text-brand" />
        <SummaryCard icon={CheckSquare} label="Attività aperte" value={counts.activities_open ?? 0} tone="bg-success/10 text-success" />
      </div>

      {/* Tabs */}
      <div className="ds-card overflow-hidden">
        <div className="flex gap-1 overflow-x-auto border-b border-border bg-slate-50 px-2">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors ${tab === t.id ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "panoramica" && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="panel-panoramica">
              <div className="space-y-4">
                <div><h3 className="ds-section-title mb-2 flex items-center gap-2"><UserCircle className="h-4 w-4 text-brand" /> Dati anagrafici</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Tipologia" value={c.type} />
                    {c.type === "Privato" ? (<><Field label="Nome" value={c.first_name} /><Field label="Cognome" value={c.last_name} /></>)
                      : (<><Field label={c.type === "Azienda" ? "Ragione sociale" : "Denominazione"} value={c.company_name} /><Field label={c.type === "Azienda" ? "Referente" : "Amministratore"} value={c.contact_person} /></>)}
                    <Field label="Codice fiscale" value={c.fiscal_code} />
                    <Field label="Partita IVA" value={c.vat_number} />
                  </div>
                </div>
                <div><h3 className="ds-section-title mb-2 flex items-center gap-2"><Phone className="h-4 w-4 text-brand" /> Contatti</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Telefono principale" value={c.phone_primary} />
                    <Field label="Telefono secondario" value={c.phone_secondary} />
                    <Field label="Email" value={c.email} />
                  </div>
                </div>
                <div><h3 className="ds-section-title mb-2 flex items-center gap-2"><MapPin className="h-4 w-4 text-brand" /> Indirizzo</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Via / Piazza" value={c.address} /><Field label="Civico" value={c.civico} />
                    <Field label="CAP" value={c.cap} /><Field label="Comune" value={c.comune} />
                    <Field label="Provincia" value={c.provincia} />
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div><h3 className="ds-section-title mb-2 flex items-center gap-2"><Building className="h-4 w-4 text-brand" /> Sede e operatore</h3>
                  <div className="grid grid-cols-2 gap-3"><Field label="Sede di riferimento" value={c.office_name} /><Field label="Operatore assegnato" value={c.operator_name} /></div>
                </div>
                <div><h3 className="ds-section-title mb-2 flex items-center gap-2"><Briefcase className="h-4 w-4 text-brand" /> Origine & date</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Origine cliente" value={c.lead_origin} />
                    <Field label="Data acquisizione" value={c.acquisition_date ? new Date(c.acquisition_date).toLocaleDateString("it-IT") : null} />
                    <Field label="Creazione" value={c.created_at ? new Date(c.created_at).toLocaleString("it-IT") : null} />
                    <Field label="Ultima modifica" value={c.updated_at ? new Date(c.updated_at).toLocaleString("it-IT") : null} />
                  </div>
                </div>
                <div><h3 className="ds-section-title mb-2 flex items-center gap-2"><Shield className="h-4 w-4 text-brand" /> Privacy (GDPR)</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge type={c.privacy_notice_provided ? "success" : "neutral"}>Informativa: {c.privacy_notice_provided ? "Fornita" : "Non fornita"}</StatusBadge>
                    <StatusBadge type={c.marketing_consent === "Si" ? "success" : c.marketing_consent === "No" ? "danger" : "neutral"}>Marketing: {c.marketing_consent || "Non espresso"}</StatusBadge>
                  </div>
                  <div className="mt-1.5 space-y-0.5 text-xs text-slate-500">
                    {c.privacy_notice_method && <div>Metodo informativa: {c.privacy_notice_method}{c.privacy_notice_version ? ` · ${c.privacy_notice_version}` : ""}{c.privacy_notice_date ? ` · ${new Date(c.privacy_notice_date).toLocaleDateString("it-IT")}` : ""}</div>}
                    {c.marketing_consent_source && <div>Fonte marketing: {c.marketing_consent_source}{c.marketing_consent_date ? ` · ${new Date(c.marketing_consent_date).toLocaleDateString("it-IT")}` : ""}</div>}
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">L'informativa non costituisce consenso generico al trattamento. Le modifiche dei consensi sono nello Storico.</p>
                </div>
                <div><h3 className="ds-section-title mb-2 flex items-center gap-2"><FileText className="h-4 w-4 text-brand" /> Note</h3>
                  <p className="whitespace-pre-wrap text-sm text-slate-700">{c.notes || <span className="text-slate-400">Nessuna nota.</span>}</p>
                </div>
                <Button onClick={() => setEditOpen(true)} variant="outline" className="gap-1.5" data-testid="panoramica-edit"><Pencil className="h-4 w-4" /> Modifica cliente</Button>
              </div>
            </div>
          )}

          {tab === "storico" && (
            <div data-testid="panel-storico">
              {history.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500">Nessun evento nello storico.</div>
              ) : (
                <ol className="relative space-y-4 border-l-2 border-border pl-5">
                  {history.map((h) => (
                    <li key={h.id} className="relative" data-testid={`history-${h.event_type}`}>
                      <span className="absolute -left-[27px] flex h-4 w-4 items-center justify-center rounded-full bg-brand ring-4 ring-white" />
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold capitalize text-slate-800">{h.event_type}</span>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3 w-3" /> {new Date(h.created_at).toLocaleString("it-IT")}</span>
                        {h.operator_name && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><UserIcon className="h-3 w-3" /> {h.operator_name}</span>}
                      </div>
                      <div className="text-sm text-slate-600">{h.description}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {tab === "luce-gas" && (
            <div data-testid="panel-luce-gas">
              <SuppliesPanel customerId={id} onChanged={reload} />
            </div>
          )}

          {tab === "efficienza" && (
            <div data-testid="panel-efficienza">
              <OpportunitiesPanel customerId={id} customerName={c.display_name} customerPhone={c.phone_primary} onChanged={reload} />
            </div>
          )}

          {tab === "preventivi" && (
            <div data-testid="panel-preventivi">
              <QuotesPanel customerId={id} onChanged={reload} />
            </div>
          )}

          {tab === "appuntamenti" && (
            <div data-testid="panel-appuntamenti">
              <AppointmentsPanel customerId={id} customerName={c.display_name} onChanged={reload} />
            </div>
          )}

          {tab === "attivita" && (
            <div data-testid="panel-attivita">
              <ActivitiesPanel customerId={id} />
            </div>
          )}

          {!["panoramica", "storico", "luce-gas", "efficienza", "preventivi", "appuntamenti", "attivita"].includes(tab) && (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid={`panel-${tab}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                {(() => { const T = TABS.find((x) => x.id === tab); const I = T?.icon || FileText; return <I className="h-6 w-6" />; })()}
              </div>
              <div className="font-display font-semibold text-slate-700">Modulo non ancora attivato.</div>
              <p className="max-w-sm text-sm text-slate-500">Questa sezione sarà disponibile in una fase successiva del CRM.</p>
            </div>
          )}
        </div>
      </div>

      <CustomerEditDialog open={editOpen} onOpenChange={setEditOpen} customer={c} onSaved={(u) => setC({ ...c, ...u })} />
    </div>
  );
}
